import React, { useState, useEffect } from 'react';
import './TeacherNotes.css';
import DashboardHeader from '../components/DashboardHeader';
import TeacherNavigationBar from '../components/TeacherNavigationBar';
import { BookIcon, SaveIcon, SendIcon, CheckIcon, ClockIcon } from '../components/Icons';

function TeacherNotes() {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTrimester, setSelectedTrimester] = useState('1');
  const [students, setStudents] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState('brouillon');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [existingNotes, setExistingNotes] = useState({});
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [teacherInfo, setTeacherInfo] = useState(null);

  // Récupérer les infos du professeur depuis localStorage
  useEffect(() => {
    // Essayer d'abord userData
    const userData = localStorage.getItem('userData');
    let teacherId = null;
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setTeacherInfo(user);
        teacherId = user.id;
      } catch (e) {
        console.error('Erreur parsing userData:', e);
      }
    }
    
    // Si pas d'ID dans userData, essayer teacher_id directement
    if (!teacherId) {
      teacherId = localStorage.getItem('teacher_id');
      
      // Charger aussi les infos du professeur depuis les autres clés
      if (teacherId) {
        setTeacherInfo({
          id: parseInt(teacherId),
          nom: localStorage.getItem('teacher_nom') || '',
          prenom: localStorage.getItem('teacher_prenom') || '',
          matiere: localStorage.getItem('teacher_matiere') || ''
        });
      }
    }
    
    console.log('=== DEBUG useEffect ===');
    console.log('teacherId:', teacherId);
    console.log('userData:', userData);
    
    if (teacherId) {
      loadTeacherClasses(teacherId);
    } else {
      console.error('❌ Aucun teacher_id trouvé dans localStorage');
    }
  }, []);

  const loadTeacherClasses = async (teacherId) => {
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/teacher/dashboard/statistiques?teacher_id=${teacherId}&annee_scolaire=2025-2026`);
      const data = await response.json();
      
      console.log('Teacher classes data:', data);
      
      if (data.classes && Array.isArray(data.classes) && data.classes.length > 0) {
        setTeacherClasses(data.classes);
      } else {
        // Aucune classe assignée
        setTeacherClasses([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      setTeacherClasses([]);
    }
  };

  const classes = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale'];
  const subjects = [
    'Mathématiques', 'Français', 'Anglais', 'Physique-Chimie', 
    'SVT', 'Histoire-Géographie', 'EPS', 'Arts Plastiques'
  ];

  useEffect(() => {
    if (selectedClass && teacherClasses.length > 0) {
      loadStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, teacherClasses]);

  useEffect(() => {
    if (selectedClass && selectedSubject && selectedTrimester) {
      loadExistingNotes();
    }
  }, [selectedClass, selectedSubject, selectedTrimester]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      
      console.log('=== DEBUG loadStudents ===');
      console.log('selectedClass:', selectedClass);
      console.log('teacherClasses:', teacherClasses);
      
      // Chercher la classe dans les classes assignées
      const classeAssignee = teacherClasses.find(c => c.nom === selectedClass);
      
      console.log('classeAssignee found:', classeAssignee);
      
      if (!classeAssignee) {
        console.error('Classe non assignée au professeur');
        console.error('selectedClass:', selectedClass);
        console.error('teacherClasses:', teacherClasses);
        setStudents([]);
        setLoading(false);
        return;
      }
      
      const url = `http://127.0.0.1:8000/api/v1/admin/classes/${encodeURIComponent(classeAssignee.nom)}/etudiants`;
      console.log('Fetching URL:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('API Response:', data);
      
      if (response.ok) {
        const studentsData = data.data || data.etudiants || [];
        console.log('Students loaded:', studentsData);
        setStudents(studentsData);
      } else {
        console.error('API Error:', data);
        setStudents([]);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingNotes = async () => {
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/admin/notes/classe?classe=${selectedClass}&trimestre=Trimestre ${selectedTrimester}&annee_scolaire=2025-2026`);
      
      if (response.ok) {
        const data = await response.json();
        const notesData = data.data || data || [];
        
        // Organiser les notes par étudiant
        const notesByStudent = {};
        const statutsSet = new Set();
        
        notesData
          .filter(note => note.matiere === selectedSubject)
          .forEach(note => {
            if (!notesByStudent[note.etudiant_id]) {
              notesByStudent[note.etudiant_id] = {};
            }
            
            // Mapper les types de notes
            if (note.type === 'devoir') {
              if (!notesByStudent[note.etudiant_id].interrogation1) {
                notesByStudent[note.etudiant_id].interrogation1 = note.note;
              } else {
                notesByStudent[note.etudiant_id].interrogation2 = note.note;
              }
            } else if (note.type === 'composition') {
              notesByStudent[note.etudiant_id].devoir = note.note;
            } else if (note.type === 'examen') {
              notesByStudent[note.etudiant_id].examen = note.note;
            }
            
            notesByStudent[note.etudiant_id].appreciation = note.observation;
            notesByStudent[note.etudiant_id].statut = note.statut;
            
            if (note.statut) {
              statutsSet.add(note.statut);
            }
          });
        
        setExistingNotes(notesByStudent);
        setNotes(notesByStudent);
        
        // Déterminer le statut global
        if (statutsSet.has('valide')) {
          setSavingStatus('valide');
        } else if (statutsSet.has('soumis')) {
          setSavingStatus('soumis');
        } else if (statutsSet.has('brouillon')) {
          setSavingStatus('brouillon');
        } else {
          setSavingStatus('brouillon');
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleNoteChange = (studentId, field, value) => {
    // Valider que la note est entre 0 et 20
    if (field !== 'appreciation' && value !== '') {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0 || numValue > 20) {
        return;
      }
    }

    setNotes(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
    
    if (savingStatus === 'soumis' || savingStatus === 'valide') {
      setSavingStatus('brouillon');
    }
  };

  const calculateMoyenne = (studentId) => {
    const studentNotes = notes[studentId];
    if (!studentNotes) return 0;
    
    const values = [
      parseFloat(studentNotes.interrogation1) || 0,
      parseFloat(studentNotes.interrogation2) || 0,
      parseFloat(studentNotes.devoir) || 0,
      parseFloat(studentNotes.examen) || 0
    ].filter(n => n > 0);
    
    if (values.length === 0) return 0;
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);
  };

  const saveDraft = async () => {
    const teacherId = localStorage.getItem('teacher_id');
    if (!teacherId) {
      alert('Erreur: Informations du professeur non disponibles');
      return;
    }

    try {
      setLoading(true);
      const notesData = students.map(student => ({
        etudiant_id: student.id,
        matiere: selectedSubject,
        trimestre: `Trimestre ${selectedTrimester}`,
        annee_scolaire: '2025-2026',
        interrogation1: notes[student.id]?.interrogation1 || null,
        interrogation2: notes[student.id]?.interrogation2 || null,
        devoir: notes[student.id]?.devoir || null,
        examen: notes[student.id]?.examen || null,
        appreciation: notes[student.id]?.appreciation || null,
        statut: 'brouillon',
        enseignant_id: parseInt(teacherId)
      }));

      const response = await fetch('http://127.0.0.1:8000/api/v1/teacher/notes/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          notes: notesData,
          classe: selectedClass
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSavingStatus('brouillon');
        alert('✅ Brouillon enregistré avec succès!\n\nVous pouvez modifier ces notes plus tard.');
        await loadExistingNotes();
      } else {
        console.error('Erreur API:', data);
        alert('❌ Erreur: ' + (data.message || 'Erreur lors de l\'enregistrement'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const submitNotes = async () => {
    const teacherId = localStorage.getItem('teacher_id');
    if (!teacherId) {
      alert('Erreur: Informations du professeur non disponibles');
      return;
    }

    try {
      setLoading(true);
      const notesData = students.map(student => ({
        etudiant_id: student.id,
        matiere: selectedSubject,
        trimestre: `Trimestre ${selectedTrimester}`,
        annee_scolaire: '2025-2026',
        interrogation1: notes[student.id]?.interrogation1 || null,
        interrogation2: notes[student.id]?.interrogation2 || null,
        devoir: notes[student.id]?.devoir || null,
        examen: notes[student.id]?.examen || null,
        appreciation: notes[student.id]?.appreciation || null,
        statut: 'soumis',
        enseignant_id: parseInt(teacherId)
      }));

      const response = await fetch('http://127.0.0.1:8000/api/v1/teacher/notes/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          notes: notesData,
          classe: selectedClass
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSavingStatus('soumis');
        setShowSubmitModal(false);
        
        let message = '✅ Notes soumises avec succès!\n\n';
        message += `• ${data.data.notes_creees} note(s) enregistrée(s)\n`;
        message += `• ${data.data.notifications_envoyees} notification(s) envoyée(s) pour notes < 10\n`;
        message += '\nL\'administrateur pourra maintenant valider ces notes.';
        
        alert(message);
        await loadExistingNotes();
      } else {
        console.error('Erreur API:', data);
        alert('❌ Erreur: ' + (data.message || 'Erreur lors de la soumission'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkIfComplete = () => {
    return students.every(student => {
      const studentNotes = notes[student.id];
      return studentNotes && 
             studentNotes.interrogation1 && 
             studentNotes.interrogation2 && 
             studentNotes.devoir && 
             studentNotes.examen;
    });
  };

  const exportToCSV = () => {
    const headers = ['Nom', 'Prénom', 'Numéro', 'Interrogation 1', 'Interrogation 2', 'Devoir', 'Examen', 'Moyenne', 'Appréciation'];
    const rows = students.map(student => [
      student.nom,
      student.prenom,
      student.numero_etudiant,
      notes[student.id]?.interrogation1 || '',
      notes[student.id]?.interrogation2 || '',
      notes[student.id]?.devoir || '',
      notes[student.id]?.examen || '',
      calculateMoyenne(student.id),
      notes[student.id]?.appreciation || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `notes_${selectedClass}_${selectedSubject}_T${selectedTrimester}.csv`;
    link.click();
  };

  const printNotes = () => {
    window.print();
  };

  const getCompletionPercentage = () => {
    if (students.length === 0) return 0;
    const completedStudents = students.filter(student => {
      const studentNotes = notes[student.id];
      return studentNotes && 
             studentNotes.interrogation1 && 
             studentNotes.interrogation2 && 
             studentNotes.devoir && 
             studentNotes.examen;
    }).length;
    return Math.round((completedStudents / students.length) * 100);
  };

  const getStatusBadge = () => {
    switch (savingStatus) {
      case 'brouillon':
        return <span className="status-badge badge-warning"><ClockIcon size={14} /> Brouillon</span>;
      case 'soumis':
        return <span className="status-badge badge-info"><SendIcon size={14} /> Soumis - En attente de validation</span>;
      case 'valide':
        return <span className="status-badge badge-success"><CheckIcon size={14} /> Validé par l'administration</span>;
      default:
        return <span className="status-badge badge-secondary">Non défini</span>;
    }
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <TeacherNavigationBar 
        title="Saisie des Notes" 
        showBack={true}
        onBack={() => window.navigateTo('teacher-dashboard')}
      />
      
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          
          {/* Instructions */}
          <section className="instructions-section">
            <div className="instructions-header">
              <h3>📋 Instructions de saisie</h3>
            </div>
            <div className="instructions-content">
              <div className="instruction-item">
                <span className="instruction-number">1</span>
                <div>
                  <strong>Sélectionner les paramètres</strong>
                  <p>Choisissez la classe, la matière et le trimestre</p>
                </div>
              </div>
              <div className="instruction-item">
                <span className="instruction-number">2</span>
                <div>
                  <strong>Saisir les notes</strong>
                  <p>Remplissez les 4 notes pour chaque élève (0-20)</p>
                </div>
              </div>
              <div className="instruction-item">
                <span className="instruction-number">3</span>
                <div>
                  <strong>Enregistrer en brouillon</strong>
                  <p>Sauvegardez votre travail à tout moment</p>
                </div>
              </div>
              <div className="instruction-item">
                <span className="instruction-number">4</span>
                <div>
                  <strong>Soumettre pour validation</strong>
                  <p>Une fois toutes les notes complètes, soumettez à l'administration</p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Sélection des paramètres */}
          <section className="selection-section">
            <h2>Paramètres de Saisie</h2>
            
            {teacherClasses.length === 0 && (
              <div className="alert alert-warning">
                <div className="alert-icon">⚠️</div>
                <div className="alert-content">
                  <strong>Aucune classe assignée</strong>
                  <p>Vous n'avez pas encore de classes assignées. Pour pouvoir saisir des notes :</p>
                  <ol>
                    <li>Contactez l'administration</li>
                    <li>L'admin doit aller dans "Attribution des classes"</li>
                    <li>Vous assigner des classes</li>
                    <li>Vos classes apparaîtront ici automatiquement</li>
                  </ol>
                </div>
              </div>
            )}
            
            {teacherInfo && (
              <div className="teacher-info-box">
                <p><strong>Enseignant:</strong> {teacherInfo.prenom} {teacherInfo.nom}</p>
                <p><strong>Matière:</strong> {teacherInfo.matiere}</p>
                <p><strong>Classes assignées:</strong> {teacherClasses.length}</p>
              </div>
            )}
            
            <div className="selection-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Classe</label>
                  <select 
                    value={selectedClass} 
                    onChange={(e) => setSelectedClass(e.target.value)}
                    disabled={teacherClasses.length === 0}
                  >
                    <option value="">
                      {teacherClasses.length === 0 
                        ? 'Aucune classe assignée' 
                        : 'Sélectionner une classe'}
                    </option>
                    {Array.isArray(teacherClasses) && teacherClasses.map((classe) => (
                      <option key={classe.id} value={classe.nom}>
                        {classe.nom} ({classe.effectif} élèves)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Matière</label>
                  <select 
                    value={selectedSubject} 
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    disabled={!teacherInfo}
                  >
                    <option value="">Sélectionner une matière</option>
                    {teacherInfo && (
                      <option value={teacherInfo.matiere}>{teacherInfo.matiere}</option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Trimestre</label>
                  <select 
                    value={selectedTrimester} 
                    onChange={(e) => setSelectedTrimester(e.target.value)}
                  >
                    <option value="1">1er Trimestre</option>
                    <option value="2">2ème Trimestre</option>
                    <option value="3">3ème Trimestre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <div>{getStatusBadge()}</div>
                </div>
              </div>
            </div>
          </section>

          {/* Tableau de saisie des notes */}
          {selectedClass && selectedSubject && students.length > 0 && (
            <section className="notes-section">
              <div className="section-header">
                <h2>Saisie des Notes - {selectedClass} - {selectedSubject}</h2>
                <div className="action-buttons">
                  <button 
                    className="btn btn-outline"
                    onClick={() => loadExistingNotes()}
                    disabled={loading}
                    title="Recharger les notes depuis la base de données"
                  >
                    🔄 Actualiser
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={exportToCSV}
                    disabled={loading || students.length === 0}
                    title="Exporter en CSV"
                  >
                    📥 Exporter
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={printNotes}
                    disabled={loading || students.length === 0}
                    title="Imprimer"
                  >
                    🖨️ Imprimer
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={() => {
                      if (window.confirm('Voulez-vous vraiment annuler toutes les modifications non enregistrées ?')) {
                        loadExistingNotes();
                      }
                    }}
                    disabled={loading || savingStatus === 'valide'}
                    title="Annuler les modifications"
                  >
                    ↩️ Annuler
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={saveDraft}
                    disabled={loading || savingStatus === 'valide'}
                    title="Enregistrer comme brouillon"
                  >
                    <SaveIcon size={16} />
                    Enregistrer Brouillon
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowSubmitModal(true)}
                    disabled={loading || !checkIfComplete() || savingStatus === 'soumis' || savingStatus === 'valide'}
                    title={!checkIfComplete() ? 'Toutes les notes doivent être remplies' : 'Soumettre à l\'administration'}
                  >
                    <SendIcon size={16} />
                    Soumettre pour Validation
                  </button>
                </div>
              </div>

              {/* Barre de progression */}
              <div className="progress-section">
                <div className="progress-info">
                  <span>Progression de saisie: <strong>{getCompletionPercentage()}%</strong></span>
                  <span>{students.filter(s => {
                    const sn = notes[s.id];
                    return sn && sn.interrogation1 && sn.interrogation2 && sn.devoir && sn.examen;
                  }).length} / {students.length} élèves complétés</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${getCompletionPercentage()}%` }}
                  ></div>
                </div>
              </div>

              {/* Légende pour les notes faibles */}
              <div className="notes-legend">
                <strong>📋 Légende :</strong>
                <div className="legend-item">
                  <div className="legend-color faible"></div>
                  <span>⚠️ Notes/moyennes &lt; 10 : Notifications automatiques envoyées aux parents</span>
                </div>
                <div className="legend-item">
                  <span>💡 Les notifications sont envoyées uniquement lors de la soumission (pas en brouillon)</span>
                </div>
              </div>

              <div className="notes-table-container">
                <table className="notes-table">
                  <thead>
                    <tr>
                      <th>Élève</th>
                      <th>Interrogation 1</th>
                      <th>Interrogation 2</th>
                      <th>Devoir</th>
                      <th>Examen</th>
                      <th>Moyenne</th>
                      <th>Appréciation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.id}>
                        <td>
                          <div className="student-info">
                            <strong>{student.nom} {student.prenom}</strong>
                            <small>{student.numero_etudiant}</small>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            step="0.25"
                            value={notes[student.id]?.interrogation1 || ''}
                            onChange={(e) => handleNoteChange(student.id, 'interrogation1', e.target.value)}
                            disabled={savingStatus === 'valide'}
                            className={`note-input ${(notes[student.id]?.interrogation1 && notes[student.id]?.interrogation1 < 10) ? 'note-faible' : ''}`}
                          />
                          {notes[student.id]?.interrogation1 && notes[student.id]?.interrogation1 < 10 && (
                            <span className="note-warning" title="Note faible - Notification envoyée">⚠️</span>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            step="0.25"
                            value={notes[student.id]?.interrogation2 || ''}
                            onChange={(e) => handleNoteChange(student.id, 'interrogation2', e.target.value)}
                            disabled={savingStatus === 'valide'}
                            className={`note-input ${(notes[student.id]?.interrogation2 && notes[student.id]?.interrogation2 < 10) ? 'note-faible' : ''}`}
                          />
                          {notes[student.id]?.interrogation2 && notes[student.id]?.interrogation2 < 10 && (
                            <span className="note-warning" title="Note faible - Notification envoyée">⚠️</span>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            step="0.25"
                            value={notes[student.id]?.devoir || ''}
                            onChange={(e) => handleNoteChange(student.id, 'devoir', e.target.value)}
                            disabled={savingStatus === 'valide'}
                            className={`note-input ${(notes[student.id]?.devoir && notes[student.id]?.devoir < 10) ? 'note-faible' : ''}`}
                          />
                          {notes[student.id]?.devoir && notes[student.id]?.devoir < 10 && (
                            <span className="note-warning" title="Note faible - Notification envoyée">⚠️</span>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            step="0.25"
                            value={notes[student.id]?.examen || ''}
                            onChange={(e) => handleNoteChange(student.id, 'examen', e.target.value)}
                            disabled={savingStatus === 'valide'}
                            className={`note-input ${(notes[student.id]?.examen && notes[student.id]?.examen < 10) ? 'note-faible' : ''}`}
                          />
                          {notes[student.id]?.examen && notes[student.id]?.examen < 10 && (
                            <span className="note-warning" title="Note faible - Notification envoyée">⚠️</span>
                          )}
                        </td>
                        <td>
                          <span className={`moyenne-display ${calculateMoyenne(student.id) < 10 ? 'moyenne-faible' : ''}`}>
                            {calculateMoyenne(student.id)}
                            {calculateMoyenne(student.id) < 10 && calculateMoyenne(student.id) > 0 && (
                              <span className="moyenne-warning" title="Moyenne faible - Notification envoyée">⚠️</span>
                            )}
                          </span>
                        </td>
                        <td>
                          <textarea
                            value={notes[student.id]?.appreciation || ''}
                            onChange={(e) => handleNoteChange(student.id, 'appreciation', e.target.value)}
                            disabled={savingStatus === 'valide'}
                            className="appreciation-input"
                            placeholder="Appréciation..."
                            rows="2"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {savingStatus === 'valide' && (
                <div className="info-message info-success">
                  <CheckIcon size={20} />
                  <div>
                    <strong>Notes validées</strong>
                    <p>Ces notes ont été validées par l'administration. Les bulletins peuvent être générés.</p>
                  </div>
                </div>
              )}

              {savingStatus === 'soumis' && (
                <div className="info-message info-info">
                  <SendIcon size={20} />
                  <div>
                    <strong>Notes soumises</strong>
                    <p>Ces notes ont été soumises à l'administration et sont en attente de validation. Vous ne pouvez plus les modifier.</p>
                  </div>
                </div>
              )}

              {savingStatus === 'brouillon' && (
                <div className="info-message info-warning">
                  <ClockIcon size={20} />
                  <div>
                    <strong>Brouillon</strong>
                    <p>Ces notes sont enregistrées comme brouillon. Vous pouvez les modifier et les soumettre quand elles seront complètes.</p>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* État vide */}
          {(!selectedClass || !selectedSubject) && (
            <section className="empty-state">
              <BookIcon size={64} color="#ccc" />
              <h3>Sélectionnez une classe et une matière</h3>
              <p>Choisissez les paramètres ci-dessus pour commencer la saisie des notes</p>
            </section>
          )}

          {/* Modal de confirmation */}
          {showSubmitModal && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>Confirmer la soumission</h3>
                <p>Êtes-vous sûr de vouloir soumettre ces notes pour validation ?</p>
                <p><strong>Une fois soumises, les notes ne pourront plus être modifiées jusqu'à validation.</strong></p>
                <div className="modal-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowSubmitModal(false)}
                  >
                    Annuler
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={submitNotes}
                    disabled={loading}
                  >
                    {loading ? 'Soumission...' : 'Confirmer'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default TeacherNotes;